import React, { useState, useEffect } from 'react';
import Button from '@/components/common/Button';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import RutInput from '@/components/common/RutInput';
import ValidatedInput from '@/components/common/ValidatedInput';
import { FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaIdCard, FaUserCircle } from 'react-icons/fa';
import { normalizeDataObject } from '@/utils/normalizationUtils';
import { usePlanValidation } from '@/hooks/usePlanValidation';
import QuotaWarning from '@/components/common/QuotaWarning';
import PaymentModal from '@/components/payments/PaymentModal';

/**
 * Formulario para crear o editar clientes
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.initialData - Datos iniciales del cliente (para edición)
 * @param {function} props.onSubmit - Función a ejecutar al enviar el formulario
 * @param {function} props.onCancel - Función a ejecutar al cancelar
 * @returns {JSX.Element} - Componente de formulario de cliente
 */
const ClienteForm = ({ initialData = {}, onSubmit, onCancel }) => {
  // Hook para validación de cuotas
  const {
    validateQuota,
    getResourceUsage,
    showPaymentModal,
    targetPlanId,
    closePaymentModal,
    openPaymentModal
  } = usePlanValidation();
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    telefono: '',
    email: '',
    direccion: '',
    rut: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Si hay datos iniciales, actualizar el estado del formulario
  useEffect(() => {
    if (initialData && Object.keys(initialData).length) {
      // Normalizar datos iniciales para tener consistencia
      const normalizedData = normalizeDataObject(initialData, 'cliente');
      
      // Separar nombre completo si viene como campo único
      let nombre = normalizedData.nombre || '';
      let apellido = normalizedData.apellido || '';
      
      if (normalizedData.Nombre && !nombre && !apellido) {
        const nombreCompleto = normalizedData.Nombre.trim().split(' ');
        nombre = nombreCompleto[0] || '';
        apellido = nombreCompleto.slice(1).join(' ') || '';
      }
      
      setFormData({
        nombre: nombre || normalizedData.Nombre?.split(' ')[0] || '',
        apellido: apellido || normalizedData.Apellido || '',
        telefono: normalizedData.telefono || normalizedData.Telefono || '',
        email: normalizedData.email || normalizedData.Email || '',
        direccion: normalizedData.direccion || normalizedData.Direccion || '',
        rut: normalizedData.rut || normalizedData.Rut || '',
      });
    }
  }, [initialData]);

  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({ ...prevData, [name]: value }));
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    // Solo validar cuota para nuevos clientes (no para edición)
    const isEditing = initialData && initialData.id;
    
    if (!isEditing) {
      // Validar cuota de clientes antes de crear
      const canCreateCliente = validateQuota('clientes', {
        showToast: false,
        showModal: true
      });
      
      if (!canCreateCliente) {
        //console.log('🚫 No se puede crear cliente - límite alcanzado');
        return; // Bloquear el envío del formulario
      }
    }
    
    setIsSubmitting(true);
    
    try {
      // Normalizar datos antes de enviar
      const normalizedFormData = normalizeDataObject(formData, 'cliente');
      await onSubmit(normalizedFormData);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Obtener métricas de uso para clientes
  const clientesUsage = getResourceUsage('clientes');
  const isEditing = initialData && initialData.id;

  return (
    <form onSubmit={handleSubmit}>
      {/* Mostrar advertencia de cuota solo para nuevos clientes */}
      {!isEditing && clientesUsage && (
        <QuotaWarning
          resourceType="clientes"
          usageMetric={clientesUsage}
          onUpgrade={() => openPaymentModal()}
          className="mb-6"
        />
      )}
      
      {/* Header con avatar y descripción */}
      {/* <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 rounded-lg mb-6">
        <div className="flex items-center space-x-4">
          <div className="bg-white bg-opacity-20 p-3 rounded-full">
            <FaUserCircle className="text-white text-2xl" />
          </div>
          <div>
            <h3 className="text-white text-lg font-semibold">
              {initialData.id ? 'Actualizar Información del Cliente' : 'Nuevo Cliente'}
            </h3>
            <p className="text-blue-100 text-sm">
              Complete la información del cliente para gestionar sus servicios
            </p>
          </div>
        </div>
      </div> */}

      <div className="md:grid md:grid-cols-2 md:gap-6">
        {/* Columna izquierda: Información Personal */}
        <div className="bg-gray-800 p-4 rounded-lg mb-6 md:mb-0 border border-gray-600">
          <div className="flex items-center mb-4 pb-2 border-b border-gray-600">
            <FaUser className="text-blue-400 mr-2" />
            <h4 className="text-white font-medium">Información Personal</h4>
          </div>
          
          {/* Nombre */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaUser className="text-gray-400 mr-2 text-xs" />
              Nombre<span className="text-red-500 ml-1">*</span>
            </label>
            <ValidatedInput
              type="text"
              name="nombre"
              placeholder="Ingresa el nombre"
              value={formData.nombre}
              onChange={handleChange}
              required
              requiredMessage="El nombre es requerido"
            />
          </div>
          
          {/* Apellido */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaUser className="text-gray-400 mr-2 text-xs" />
              Apellido<span className="text-red-500 ml-1">*</span>
            </label>
            <ValidatedInput
              type="text"
              name="apellido"
              placeholder="Ingresa el apellido"
              value={formData.apellido}
              onChange={handleChange}
              required
              requiredMessage="El apellido es requerido"
            />
          </div>
          
          {/* RUT */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaIdCard className="text-gray-400 mr-2 text-xs" />
              RUT<span className="text-red-500 ml-1">*</span>
            </label>
            <RutInput
              name="rut"
              placeholder="12.345.678-9"
              value={formData.rut}
              onChange={handleChange}
              required
              requiredMessage="El RUT es requerido"
            />
          </div>
        </div>
        
        {/* Columna derecha: Información de Contacto */}
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-600">
          <div className="flex items-center mb-4 pb-2 border-b border-gray-600">
            <FaPhone className="text-green-400 mr-2" />
            <h4 className="text-white font-medium">Información de Contacto</h4>
          </div>
          
          {/* Teléfono */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaPhone className="text-gray-400 mr-2 text-xs" />
              Teléfono<span className="text-red-500 ml-1">*</span>
            </label>
            <ValidatedInput
              type="tel"
              name="telefono"
              placeholder="+56 9 1234 5678"
              value={formData.telefono}
              onChange={handleChange}
              required
              requiredMessage="El teléfono es requerido"
            />
          </div>
          
          {/* Email */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaEnvelope className="text-gray-400 mr-2 text-xs" />
              Correo Electrónico
            </label>
            <ValidatedInput
              type="email"
              name="email"
              placeholder="correo@ejemplo.com"
              value={formData.email}
              onChange={handleChange}
            />
          </div>
          
          {/* Dirección */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaMapMarkerAlt className="text-gray-400 mr-2 text-xs" />
              Dirección
            </label>
            <AddressAutocomplete
              name="direccion"
              placeholder="Ingresa la dirección completa"
              initialValue={formData.direccion}
              labelClassName="sr-only"
              inputClassName="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
              dropdownAbove={true}
              onAddressSelect={(locationData) => {
                setFormData(prevData => ({
                  ...prevData,
                  direccion: locationData.address
                }));
              }}
            />
          </div>
        </div>
      </div>
      
      <div className="border-t border-gray-700 pt-4 mt-6">
        <div className="flex flex-col md:flex-row gap-3 md:justify-end">
          <Button 
            variant="secondary" 
            onClick={onCancel}
            className="w-full md:w-auto py-2.5 md:px-4 order-1"
          >
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
            className="w-full md:w-auto py-2.5 md:px-4 order-2"
          >
            {initialData.id ? 'Actualizar Cliente' : 'Guardar Cliente'}
          </Button>
        </div>
      </div>
      
      {/* Modal de pago para upgrade de plan */}
      <PaymentModal 
        isOpen={showPaymentModal}
        onClose={closePaymentModal}
        targetPlanId={targetPlanId}
      />
    </form>
  );
};

export default ClienteForm;